/**
 * Theme utility functions for dark mode implementation
 * Handles theme detection, application, and persistence
 */

export type Theme = 'light' | 'dark' | 'system';

/**
 * Get the system theme preference
 */
export const getSystemTheme = (): 'light' | 'dark' => {
  if (typeof window === 'undefined') return 'light';

  return window.matchMedia('(prefers-color-scheme: dark)').matches ? 'dark' : 'light';
};

/**
 * Get stored theme preference from localStorage
 */
export const getStoredTheme = (): Theme | null => {
  if (typeof window === 'undefined') return null;

  const stored = localStorage.getItem('vehix-theme');
  if (stored === 'light' || stored === 'dark' || stored === 'system') {
    return stored;
  }
  return null;
};

/**
 * Store theme preference in localStorage
 */
export const setStoredTheme = (theme: Theme): void => {
  if (typeof window === 'undefined') return;

  localStorage.setItem('vehix-theme', theme);
};

/**
 * Apply theme to document root
 */
export const applyTheme = (theme: 'light' | 'dark'): void => {
  const root = document.documentElement;

  if (theme === 'dark') {
    root.classList.add('dark');
    root.style.colorScheme = 'dark';
  } else {
    root.classList.remove('dark');
    root.style.colorScheme = 'light';
  }
};

/**
 * Get the resolved theme (actual theme being used)
 */
export const getResolvedTheme = (theme: Theme): 'light' | 'dark' => {
  if (theme === 'system') {
    return getSystemTheme();
  }
  return theme;
};

/**
 * Initialize theme on page load (for SSR flash prevention)
 */
export const initializeTheme = (): 'light' | 'dark' => {
  const storedTheme = getStoredTheme();
  const theme = storedTheme || 'system';
  const resolvedTheme = getResolvedTheme(theme);

  applyTheme(resolvedTheme);
  return resolvedTheme;
};

/**
 * Listen to system theme changes
 */
export const watchSystemTheme = (callback: (theme: 'light' | 'dark') => void): (() => void) => {
  if (typeof window === 'undefined') return () => {};

  const mediaQuery = window.matchMedia('(prefers-color-scheme: dark)');

  const handleChange = (e: MediaQueryListEvent) => {
    callback(e.matches ? 'dark' : 'light');
  };

  mediaQuery.addEventListener('change', handleChange);

  return () => mediaQuery.removeEventListener('change', handleChange);
};
